import * as React from 'react';
import * as Popover from '@radix-ui/react-popover';
import { CalendarDays } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Calendar } from '@/components/ui/calendar';
import { cn } from '@/lib/utils';

export interface DatePickerProps extends React.InputHTMLAttributes<HTMLInputElement> {
  /** Accessible label for the calendar trigger button. */
  triggerLabel?: string;
}

/**
 * Date selector. The field keeps the native date input (so registration,
 * validation, min/max, placeholders and the existing API format are
 * untouched) and adds a calendar popover that opens from a calendar button.
 * Selecting a day writes the zero-padded YYYY-MM-DD value into the input and
 * fires the input's onChange, so react-hook-form and controlled parents
 * update exactly as if the user typed the date.
 */
export const DatePicker = React.forwardRef<HTMLInputElement, DatePickerProps>(function DatePicker(
  { triggerLabel = 'Open calendar', className, ...props },
  forwardedRef
) {
  const [open, setOpen] = React.useState(false);
  const inputRef = React.useRef<HTMLInputElement | null>(null);

  const mergedRef = React.useCallback(
    (node: HTMLInputElement | null) => {
      inputRef.current = node;
      if (typeof forwardedRef === 'function') {
        forwardedRef(node);
      } else if (forwardedRef && typeof forwardedRef === 'object') {
        (forwardedRef as React.MutableRefObject<HTMLInputElement | null>).current = node;
      }
    },
    [forwardedRef]
  );

  const showTrigger = !props.disabled && !props.readOnly;

  const handleSelect = (value: string) => {
    setOpen(false);
    const el = inputRef.current;
    if (!el) return;
    if (value !== el.value) {
      el.value = value;
      props.onChange?.({ target: el } as React.ChangeEvent<HTMLInputElement>);
    }
  };

  const isControlled = props.value !== undefined && props.value !== null;
  const currentValue = isControlled ? String(props.value) : inputRef.current?.value ?? '';
  const minValue = props.min !== undefined && props.min !== null ? String(props.min) : undefined;
  const maxValue = props.max !== undefined && props.max !== null ? String(props.max) : undefined;

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <div className="relative min-w-0">
        <Input
          ref={mergedRef}
          {...props}
          type="date"
          value={isControlled ? String(props.value) : undefined}
          className={cn(showTrigger && 'pr-9', className)}
        />
        {showTrigger && (
          <Popover.Trigger asChild>
            <button
              type="button"
              aria-label={triggerLabel}
              onClick={() => setOpen(true)}
              className="absolute inset-y-0 right-0 z-10 flex w-9 items-center justify-center rounded-r-md text-muted transition-colors hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
            >
              <CalendarDays size={16} />
            </button>
          </Popover.Trigger>
        )}
      </div>
      <Popover.Portal>
        <Popover.Content
          align="start"
          sideOffset={4}
          className="z-[60] w-[17rem] max-w-[calc(100vw-2rem)] rounded-lg border bg-surface p-2 text-primary shadow-md focus-visible:outline-none"
        >
          <Calendar
            value={currentValue}
            onSelect={handleSelect}
            min={minValue}
            max={maxValue}
            disabled={props.disabled}
          />
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
});